/** Pure Versions history (تاریخچه انتشار) display mapping — no I/O. D5C. */

import type { StatusTone } from "./overview-ops";

export type VersionSnapshotRow = {
  id: string;
  contentItemId: string;
  kind: string;
  title: string;
  version: number;
  publishedAt: string;
  publishedBy: string;
  payloadChecksum: string;
  rollbackOfSnapshotId: string | null;
};

export type VersionSortKey = "time" | "version" | "kind" | "title" | "actor";

export type StatusDisplay = { label: string; tone: StatusTone; detail?: string };

export const VERSIONS_PAGE_LABEL = "تاریخچه انتشار";

export const VERSIONS_PAGE_NOTE =
  "تاریخچه انتشار نسخه‌های نشر محتوای CMS (اطلاعیه‌ها / پیشنهادها) را نشان می‌دهد. این صفحه تاریخچهٔ محتوا است — نه Bundle پورتال و نه RC منجمد هات‌اسپات.";

export const VERSIONS_RC_DISTINCTION_NOTE =
  "RC و Bundle منجمد را در «Bundle» و ناوگان ببینید. اینجا بازگردانی محتوا نسخهٔ جدید می‌سازد و ZIP/RouterOS را تغییر نمی‌دهد.";

export const VERSION_KIND_LABELS: Record<string, string> = {
  promotion: "پیشنهادها",
  announcement: "اطلاعیه‌ها",
  sponsored: "تبلیغات ویژه",
};

export function versionKindLabel(kind: string): string {
  return VERSION_KIND_LABELS[kind] ?? kind;
}

export function versionKindDisplay(kind: string): StatusDisplay {
  const label = versionKindLabel(kind);
  if (kind === "promotion") return { label, tone: "info", detail: "نقشه فنی: promotion" };
  if (kind === "announcement") return { label, tone: "success", detail: "نقشه فنی: announcement" };
  if (kind === "sponsored") return { label, tone: "warning", detail: "نقشه فنی: sponsored" };
  return { label, tone: "neutral", detail: `نقشه فنی: ${kind}` };
}

/** Integrity/verification state from existing snapshot fields only. */
export function versionIntegrityDisplay(row: VersionSnapshotRow): StatusDisplay {
  if (!row.payloadChecksum) {
    return { label: "بدون checksum", tone: "warning", detail: "مقدار یکپارچگی در رکورد نیست" };
  }
  if (row.rollbackOfSnapshotId) {
    return {
      label: "بازگردانی ثبت‌شده",
      tone: "warning",
      detail: "این نسخه از بازگردانی یک اسنپ‌شات قبلی ایجاد شده؛ checksum موجود است",
    };
  }
  return {
    label: "checksum ثبت‌شده",
    tone: "success",
    detail: "یکپارچگی payload با checksum موجود — نه تأیید RC منجمد",
  };
}

export function versionLifecycleDisplay(row: VersionSnapshotRow): StatusDisplay {
  if (row.rollbackOfSnapshotId) {
    return {
      label: "نسخهٔ بازگردانی",
      tone: "warning",
      detail: "نتیجه بازگردانی — نسخهٔ جدید در تاریخچه",
    };
  }
  return {
    label: "نسخهٔ انتشار",
    tone: "success",
    detail: "اسنپ‌شات انتشار محتوا",
  };
}

export function shortChecksum(checksum: string, len = 12): string {
  if (!checksum) return "—";
  return checksum.length <= len ? checksum : `${checksum.slice(0, len)}…`;
}

export function matchesVersionQuery(row: VersionSnapshotRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [
    row.title,
    row.kind,
    versionKindLabel(row.kind),
    String(row.version),
    row.publishedBy,
    row.id,
    row.contentItemId,
    row.payloadChecksum,
    row.rollbackOfSnapshotId ? "rollback بازگردانی" : "",
  ]
    .join(" ")
    .toLowerCase()
    .includes(q);
}

export function matchesVersionFilter(row: VersionSnapshotRow, filter: string): boolean {
  if (filter === "all") return true;
  if (filter === "promotion" || filter === "announcement" || filter === "sponsored") {
    return row.kind === filter;
  }
  if (filter === "rollback") return Boolean(row.rollbackOfSnapshotId);
  if (filter === "publish") return !row.rollbackOfSnapshotId;
  return true;
}

export function filterAndSortVersions(
  rows: VersionSnapshotRow[],
  opts: { query: string; filter: string; sort: VersionSortKey },
): VersionSnapshotRow[] {
  const filtered = rows.filter(
    (r) => matchesVersionQuery(r, opts.query) && matchesVersionFilter(r, opts.filter),
  );
  const sorted = [...filtered];
  sorted.sort((a, b) => {
    if (opts.sort === "version") return b.version - a.version;
    if (opts.sort === "kind") {
      const kindCmp = a.kind.localeCompare(b.kind);
      return kindCmp !== 0 ? kindCmp : b.publishedAt.localeCompare(a.publishedAt);
    }
    if (opts.sort === "title") return a.title.localeCompare(b.title, "fa");
    if (opts.sort === "actor") return a.publishedBy.localeCompare(b.publishedBy, "fa");
    return b.publishedAt.localeCompare(a.publishedAt);
  });
  return sorted;
}

export type RelatedVersionsLink = { id: string; label: string; to: string };

export function relatedVersionsLinks(kind?: string): RelatedVersionsLink[] {
  const links: RelatedVersionsLink[] = [
    { id: "bundles", label: "Bundle (RC منجمد)", to: "/portal-bundles" },
    { id: "fleet", label: "ناوگان و استقرار", to: "/fleet" },
    { id: "promotions", label: "پیشنهادها", to: "/promotions" },
    { id: "announcements", label: "اطلاعیه‌ها", to: "/announcements" },
    { id: "overview", label: "نمای کلی", to: "/" },
  ];
  if (kind === "promotion") {
    return [
      { id: "promotions", label: "پیشنهادها", to: "/promotions" },
      ...links.filter((l) => l.id !== "promotions"),
    ];
  }
  if (kind === "announcement") {
    return [
      { id: "announcements", label: "اطلاعیه‌ها", to: "/announcements" },
      ...links.filter((l) => l.id !== "announcements"),
    ];
  }
  return links;
}
